import { UserManager, WebStorageStateStore } from 'oidc-client-ts';

/**
 * Runtime config, fetched (not baked into the bundle) so the same build works
 * for any environment. scripts/deploy-ui.sh regenerates /app-config.json in the
 * S3 bucket from config/config.json's `oidc` block on every deploy.
 */
export async function loadRuntimeConfig() {
  const res = await fetch('./app-config.json', { cache: 'no-store' });
  if (!res.ok) {
    throw new Error(`Could not load app-config.json (${res.status}).`);
  }
  return res.json();
}

/**
 * Authorization Code + PKCE against the external OIDC provider (CloudIDP).
 * Public client - no client secret. The redirect URI is the app's own origin
 * (single-page at "/"); register `https://wltp-aws-health-events.vwapps.run/` in CloudIDP.
 */
export function createUserManager(config) {
  return new UserManager({
    authority: config.oidc.issuer,
    client_id: config.oidc.clientId,
    redirect_uri: `${window.location.origin}/`,
    post_logout_redirect_uri: `${window.location.origin}/`,
    response_type: 'code',
    scope: config.oidc.scopes,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    automaticSilentRenew: true,
  });
}

/** True when the current URL is an OIDC redirect callback (carries ?code&state). */
export function isAuthCallback() {
  const params = new URLSearchParams(window.location.search);
  return params.has('code') && params.has('state');
}

/** Strip the OIDC callback params from the URL bar after completing sign-in. */
export function clearAuthCallbackFromUrl() {
  window.history.replaceState({}, document.title, window.location.pathname);
}

/**
 * Every role/group claim shape Keycloak may put the signed-in user's roles under, merged into one
 * Set. Checked defensively across shapes (not just one) since the ID token here carries roles as a
 * flat `groups` claim while the access token carries the same roles under `resource_access.*.roles`
 * (see lambdas/health_events_web_authorizer/handler.py's `_has_required_role` - the actual
 * enforcement point; this is only used here for an early, friendlier UI message).
 */
function extractRoles(profile) {
  const roles = new Set(profile?.groups ?? []);
  for (const role of profile?.realm_access?.roles ?? []) roles.add(role);
  for (const clientRoles of Object.values(profile?.resource_access ?? {})) {
    for (const role of clientRoles?.roles ?? []) roles.add(role);
  }
  return roles;
}

/**
 * True if the signed-in user's ID token carries at least one of the `requiredRoles`
 * (config.oidc.requiredRoles - see config/config.json; an array, not a single string - user must
 * have ANY one of them). The API is the real enforcement point (a 401/403 there is handled
 * regardless); this only lets the SPA show a clear message up front instead of an empty/failed
 * events table. Fails open (`true`) if `requiredRoles` itself is unset or empty, so a
 * runtime-config mistake here can't lock out every user on its own.
 */
export function hasRequiredRole(user, requiredRoles) {
  if (!requiredRoles || requiredRoles.length === 0) return true;
  const userRoles = extractRoles(user?.profile);
  return requiredRoles.some((role) => userRoles.has(role));
}
